@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginefinanzen

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.coroutines.launch
import kotlinx.browser.document
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.example.project.engineletter.RenderInvoiceLetterSys
import org.example.project.engineletter.renderLetterDocumentPdfFile
import org.example.project.engineservice.ServiceApi
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.settings.LogoSettings
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.screens_sys.UnternehmenstammApi
import org.example.project.screens_sys.KundenverbrauchApi
import org.example.project.service.LoginService
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

private const val RECHNUNG_MWST_SATZ = 0.19

private fun rechnungSnapshotValue(container: All?, key: String): String =
    when (key) {
        "gesamtbetragEuro" -> container?.gesamtbetragEuro.orEmpty()
        "onboardingPreis" -> container?.onboardingPreis.orEmpty()
        "lizensierungPreis" -> container?.lizensierungPreis.orEmpty()
        "crStd" -> container?.crStd.orEmpty()
        "crKosten" -> container?.crKosten.orEmpty()
        "supportTickets" -> container?.supportTickets.orEmpty()
        "supportKosten" -> container?.supportKosten.orEmpty()
        "meetingStd" -> container?.meetingStd.orEmpty()
        "meetingKosten" -> container?.meetingKosten.orEmpty()
        "schulungStd" -> container?.schulungStd.orEmpty()
        "schulungKosten" -> container?.schulungKosten.orEmpty()
        "beratungStd" -> container?.beratungStd.orEmpty()
        "beratungKosten" -> container?.beratungKosten.orEmpty()
        "istAbgeschlossen" -> container?.webKontrolleAbgeschlossen?.takeIf { it }?.let { "Ja" }.orEmpty()
        "abgeschlossenAm" -> container?.abgeschlossenAm.orEmpty()
        else -> ""
    }

private fun rechnungParseBetrag(value: String): Double {
    val normalized = value.trim()
        .replace("EUR", "", ignoreCase = true)
        .replace("€", "")
        .replace(".", "")
        .replace(",", ".")
        .trim()
    return normalized.toDoubleOrNull() ?: 0.0
}

private fun rechnungFormatBetrag(value: Double): String =
    (js("new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format")(value) as String)

private fun rechnungNetto(container: All?): Double =
    rechnungParseBetrag(rechnungSnapshotValue(container, "gesamtbetragEuro"))

private fun rechnungMwst(container: All?): Double =
    rechnungNetto(container) * RECHNUNG_MWST_SATZ

private fun rechnungBrutto(container: All?): Double =
    rechnungNetto(container) + rechnungMwst(container)

private fun rechnungEmptySnapshot(kunde: All, monthKey: String) = All(
    id = "rechnung_${monthKey}_${kunde.firmenId}",
    firmenId = kunde.firmenId,
    firma = kunde.firma,
    lizenzkey = kunde.lizenzkey,
    datum = monthKey,
    verbrauch = emptyList()
)

private fun rechnungPdfFileName(rechnung: All): String =
    "${rechnung.id.ifBlank { "${rechnung.firmenId}_${rechnung.datum}" }}_rechnung.pdf"

@Composable
fun Sys_Rechnungsuebersicht() {
    var kunden by remember { mutableStateOf<List<All>>(emptyList()) }
    var unternehmen by remember { mutableStateOf(All()) }
    var currentSysUser by remember { mutableStateOf(All()) }
    var logoSrc by remember { mutableStateOf("") }
    var aktuellerMonatByKunde by remember { mutableStateOf<Map<String, All>>(emptyMap()) }
    var monatsrechnungenByKunde by remember { mutableStateOf<Map<String, List<All>>>(emptyMap()) }
    var selectedKundeId by remember { mutableStateOf<String?>(null) }
    var previewRechnungId by remember { mutableStateOf<String?>(null) }
    var triggerPdfKunde by remember { mutableStateOf<All?>(null) }
    var triggerPdfRechnung by remember { mutableStateOf<All?>(null) }
    val scope = rememberCoroutineScope()

    val monthKey = remember {
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        "${now.year}_${now.monthNumber.toString().padStart(2, '0')}"
    }

    suspend fun loadKundenFromNetzwerk(): List<All> {
        val lizByFirmenId = KundenverbrauchApi.loadAllDetails()
            .associateBy { it.firmenId.ifBlank { it.id } }

        return KundenverbrauchApi.loadCompanies()
            .filter { it.istkunde }
            .sortedBy { it.firma.lowercase() }
            .map { firma ->
                val firmenId = firma.firmenId.ifBlank { firma.id }
                val settings = lizByFirmenId[firmenId]
                firma.copy(
                    id = firmenId,
                    firmenId = firmenId,
                    lizenzkey = settings?.lizenzkey.orEmpty(),
                    nodePort = settings?.nodePort.orEmpty(),
                    rechnungsdatum = settings?.rechnungsdatum.orEmpty().ifBlank { "1" },
                    zustand = if (settings?.gesperrt == true) ZustandManager.Kunde.GESPERRT else firma.zustand
                )
            }
    }

    suspend fun loadAktuellerMonatsverbrauch(kundenListe: List<All>): Map<String, All> =
        kundenListe.associate { kunde ->
            val loaded = KundenverbrauchApi.loadVerbrauch(kunde.firmenId, monthKey)
            kunde.id to (loaded ?: rechnungEmptySnapshot(kunde, monthKey)).copy(
                firmenId = kunde.firmenId,
                firma = kunde.firma,
                lizenzkey = loaded?.lizenzkey.orEmpty().ifBlank { kunde.lizenzkey },
                datum = monthKey
            )
        }

    suspend fun loadMonatsrechnungen(kunde: All): List<All> {
        val loaded = KundenverbrauchApi.loadVerbrauchMonate(kunde.firmenId)
        return loaded
            .ifEmpty { listOf(aktuellerMonatByKunde[kunde.id] ?: rechnungEmptySnapshot(kunde, monthKey)) }
            .map { rechnung ->
                rechnung.copy(
                    firmenId = kunde.firmenId,
                    firma = kunde.firma,
                    lizenzkey = rechnung.lizenzkey.ifBlank { kunde.lizenzkey },
                    datum = rechnung.datum.ifBlank { rechnung.monat }
                )
            }
            .sortedByDescending { it.datum }
    }

    fun selectKundeAndLoadMonate(kundeId: String) {
        selectedKundeId = kundeId
        previewRechnungId = null
        val kunde = kunden.firstOrNull { it.id == kundeId } ?: return
        scope.launch {
            val rechnungen = loadMonatsrechnungen(kunde)
            monatsrechnungenByKunde = monatsrechnungenByKunde + (kunde.id to rechnungen)
        }
    }

    fun mapRechnungRow(kunde: All, rechnung: All?): List<String> = listOf(
        rechnung?.id.orEmpty().ifBlank { "${kunde.firmenId}_$monthKey" },
        kunde.firmenId,
        kunde.firma,
        kunde.lizenzkey,
        kunde.rechnungsdatum.ifBlank { "1" },
        rechnung?.datum.orEmpty().ifBlank { monthKey },
        rechnungSnapshotValue(rechnung, "onboardingPreis"),
        rechnungSnapshotValue(rechnung, "lizensierungPreis"),
        rechnungSnapshotValue(rechnung, "crStd"),
        rechnungSnapshotValue(rechnung, "crKosten"),
        rechnungSnapshotValue(rechnung, "supportTickets"),
        rechnungSnapshotValue(rechnung, "supportKosten"),
        rechnungSnapshotValue(rechnung, "meetingStd"),
        rechnungSnapshotValue(rechnung, "meetingKosten"),
        rechnungSnapshotValue(rechnung, "schulungStd"),
        rechnungSnapshotValue(rechnung, "schulungKosten"),
        rechnungSnapshotValue(rechnung, "beratungStd"),
        rechnungSnapshotValue(rechnung, "beratungKosten"),
        rechnungFormatBetrag(rechnungNetto(rechnung)),
        rechnungFormatBetrag(rechnungMwst(rechnung)),
        rechnungFormatBetrag(rechnungBrutto(rechnung))
    )

    fun mapMonatsRow(rechnung: All): List<String> = listOf(
        rechnung.id.ifBlank { "${rechnung.firmenId}_${rechnung.datum}" },
        rechnung.datum,
        rechnungSnapshotValue(rechnung, "istAbgeschlossen"),
        rechnungSnapshotValue(rechnung, "abgeschlossenAm"),
        rechnungSnapshotValue(rechnung, "onboardingPreis"),
        rechnungSnapshotValue(rechnung, "lizensierungPreis"),
        rechnungSnapshotValue(rechnung, "crStd"),
        rechnungSnapshotValue(rechnung, "crKosten"),
        rechnungSnapshotValue(rechnung, "supportTickets"),
        rechnungSnapshotValue(rechnung, "supportKosten"),
        rechnungSnapshotValue(rechnung, "meetingStd"),
        rechnungSnapshotValue(rechnung, "meetingKosten"),
        rechnungSnapshotValue(rechnung, "schulungStd"),
        rechnungSnapshotValue(rechnung, "schulungKosten"),
        rechnungSnapshotValue(rechnung, "beratungStd"),
        rechnungSnapshotValue(rechnung, "beratungKosten"),
        rechnungFormatBetrag(rechnungNetto(rechnung)),
        rechnungFormatBetrag(rechnungMwst(rechnung)),
        rechnungFormatBetrag(rechnungBrutto(rechnung))
    )

    LaunchedEffect(Unit) {
        logoSrc = LogoSettings.invoiceOverviewLogo
        currentSysUser = LoginService.getUser() ?: All()
        unternehmen = runCatching { UnternehmenstammApi.loadUnternehmen() ?: All() }.getOrDefault(All())
        val geladeneKunden = runCatching { loadKundenFromNetzwerk() }.getOrDefault(emptyList())
        kunden = geladeneKunden
        aktuellerMonatByKunde = runCatching { loadAktuellerMonatsverbrauch(geladeneKunden) }.getOrDefault(emptyMap())
        geladeneKunden.firstOrNull()?.let { kunde ->
            selectedKundeId = kunde.id
            monatsrechnungenByKunde = monatsrechnungenByKunde + (
                kunde.id to runCatching { loadMonatsrechnungen(kunde) }.getOrDefault(emptyList())
            )
        }
    }

    val selectedKunde = kunden.firstOrNull { it.id == selectedKundeId }
    val previewRechnung = selectedKunde?.let { kunde ->
        monatsrechnungenByKunde[kunde.id].orEmpty().firstOrNull {
            it.id.ifBlank { "${kunde.firmenId}_${it.datum}" } == previewRechnungId
        }
    }

    LaunchedEffect(triggerPdfRechnung?.id, triggerPdfRechnung?.datum) {
        val exportRechnung = triggerPdfRechnung ?: return@LaunchedEffect
        val root = document.getElementById("sys-rechnung-export-root") as? HTMLElement ?: return@LaunchedEffect
        val fileName = rechnungPdfFileName(exportRechnung)
        val pdfFile = renderLetterDocumentPdfFile(root, fileName)
        val entryId = exportRechnung.id.ifBlank { "${exportRechnung.firmenId}_${exportRechnung.datum}" }
        val uploadedUrl = ServiceApi.uploadServiceDocument("rechnung", entryId, pdfFile, fileName)
        if (uploadedUrl.isNotBlank()) {
            ServiceApi.openServiceDocument("rechnung", entryId, fileName)
        } else {
            AppDialogs.alert("Rechnung konnte nicht gespeichert werden.")
        }
        triggerPdfKunde = null
        triggerPdfRechnung = null
    }

    Div({
        style {
            height(100.percent)
            width(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            overflow("hidden")
        }
    }) {
        TabularSplitScreen(
            initialRatio = 0.56f,
            screen1 = {
                TabularScreen(
                    screenKey = "Rechnungsuebersicht",
                    rollenKey = "Rechnungsuebersicht",
                    title = "Ausgangsrechnungen aktuell ($monthKey)",
                    headers = listOf(
                        "ID", "Firmen-ID", "Firma", "Lizenzkey", "Abrechnungstag", "Monat",
                        "Onboarding Preis", "Lizensierung Preis",
                        "CR STD", "CR Kosten",
                        "SUP-Ticket", "Support Kosten",
                        "Meeting STD", "Meeting Kosten",
                        "Schulung STD", "Schulung Kosten",
                        "Beratung STD", "Beratung Kosten",
                        "Gesamt Netto", "MwSt. 19 %", "Gesamt Brutto"
                    ),
                    data = kunden.map { kunde -> kunde.id to mapRechnungRow(kunde, aktuellerMonatByKunde[kunde.id]) },
                    editable = false,
                    includeMetadata = true,
                    allowCreate = false,
                    allowDelete = false,
                    extraNavButtons = listOf(
                        TabularToolbarButton(
                            label = "Rechnung triggern",
                            icon = "images/pdf.svg",
                            enabled = selectedKunde != null,
                            onClick = {
                                selectedKunde?.let { kunde ->
                                    triggerPdfKunde = kunde
                                    triggerPdfRechnung = aktuellerMonatByKunde[kunde.id] ?: rechnungEmptySnapshot(kunde, monthKey)
                                }
                            }
                        )
                    ),
                    onRowClick = { id ->
                        selectKundeAndLoadMonate(id)
                    },
                    onSearchWithFilters = { filters ->
                        scope.launch {
                            val rows = KundenverbrauchApi.searchAbrechnung(monthKey, filters)
                            kunden = rows
                            aktuellerMonatByKunde = rows.associateBy { it.id }
                            rows.firstOrNull()?.let { kunde ->
                                selectedKundeId = kunde.id
                                previewRechnungId = null
                                monatsrechnungenByKunde = monatsrechnungenByKunde + (
                                    kunde.id to loadMonatsrechnungen(kunde)
                                )
                            }
                        }
                    },
                    onCommit = {}
                )
            },
            screen2 = selectedKundeId?.let {
                {
                    val kunde = kunden.firstOrNull { it.id == selectedKundeId }
                    if (kunde != null) {
                        val monatsrechnungen = monatsrechnungenByKunde[kunde.id].orEmpty()
                        TabularScreen(
                            screenKey = "Rechnungsuebersicht_${kunde.id}",
                            rollenKey = "Rechnungsuebersicht",
                            title = "Monate ${kunde.firma.ifBlank { kunde.firmenId }}",
                            headers = listOf(
                                "ID", "Monat", "Abgeschlossen", "Abgeschlossen am",
                                "Onboarding Preis", "Lizensierung Preis",
                                "CR STD", "CR Kosten",
                                "SUP-Ticket", "Support Kosten",
                                "Meeting STD", "Meeting Kosten",
                                "Schulung STD", "Schulung Kosten",
                                "Beratung STD", "Beratung Kosten",
                                "Gesamt Netto", "MwSt. 19 %", "Gesamt Brutto"
                            ),
                            data = monatsrechnungen.map { rechnung ->
                                rechnung.id.ifBlank { "${kunde.firmenId}_${rechnung.datum}" } to mapMonatsRow(rechnung)
                            },
                            editable = false,
                            includeMetadata = true,
                            allowCreate = false,
                            allowDelete = false,
                            allowSave = false,
                            nonEditableCols = setOf("ID"),
                            extraNavButtons = listOf(
                                TabularToolbarButton(
                                    label = "Rechnung triggern",
                                    icon = "images/pdf.svg",
                                    enabled = previewRechnung != null,
                                    onClick = {
                                        previewRechnung?.let { rechnung ->
                                            triggerPdfKunde = kunde
                                            triggerPdfRechnung = rechnung
                                        }
                                    }
                                )
                            ),
                            onRowClick = { rowId -> previewRechnungId = rowId },
                            onRowDoubleClick = { rowId -> previewRechnungId = rowId },
                            onSearchWithFilters = { filters ->
                                scope.launch {
                                    monatsrechnungenByKunde = monatsrechnungenByKunde + (
                                        kunde.id to KundenverbrauchApi.searchRechnungsMonate(kunde.firmenId, filters)
                                    )
                                }
                            },
                            onCommit = {}
                        )
                    }
                }
            }
        )

        if (triggerPdfKunde != null && triggerPdfRechnung != null) {
            Div({
                id("sys-rechnung-export-root")
                style {
                    position(Position.Fixed)
                    left((-10000).px)
                    top(0.px)
                    width(794.px)
                    property("z-index", "-1")
                    property("pointer-events", "none")
                }
            }) {
                RenderInvoiceLetterSys(
                    rechnung = triggerPdfRechnung ?: All(),
                    kunde = triggerPdfKunde ?: All(),
                    unternehmen = unternehmen,
                    logoSrc = logoSrc,
                    currentSysUser = currentSysUser
                )
            }
        }

    }
}
